import React, { useCallback, useEffect, useState } from 'react';
import { formatMoney } from '../../utils/formatMoney';
import { Undo2, Loader2, AlertCircle } from 'lucide-react';
import { tablePaymentsApi, DineInApiError, TablePayment, TablePaymentsSummary, VoidPaymentResult } from '../../services/dineInApi';

/**
 * Οι πληρωμές του τραπεζιού στην οθόνη του ΤΑΜΙΑ, με «Ακύρωση λάθος πληρωμής» (αποφάσεις
 * ιδιοκτήτη 5/10 και 6/10/2026 — από 6/10 μόνιμη, χωρίς διακόπτη, για μετρητά ΚΑΙ «Επί πιστώσει»).
 *
 * Ο server αποφασίζει τι ακυρώνεται (payments[].voidable): μόνο πληρωμές χωρίς τερματικό (μετρητά,
 * «Επί πιστώσει»), μόνο μέσα στο ανοιχτό ταμείο, όχι ήδη ακυρωμένη. Κάρτα/IRIS: η επιστροφή γίνεται
 * στο τερματικό — το λέμε δίπλα στην πληρωμή. Ο λόγος είναι προαιρετικός (απόφαση 6/10/2026, όπως σε
 * κέρασμα/έκπτωση)· ποιος/πότε (και γιατί, αν γράφτηκε) φαίνονται στην ακυρωμένη γραμμή. Χωρίς πληρωμές
 * δεν δείχνεται τίποτα.
 */

interface Props {
  storeId: number;
  sessionId: number;
  /** dine_in_void_payment (δικό του δικαίωμα από 6/10/2026) — χωρίς αυτό η λίστα φαίνεται χωρίς «Ακύρωση». */
  canVoid: boolean;
  /** Αλλάζει όταν καταγράφεται πληρωμή στο ίδιο παράθυρο → ξαναφορτώνει. */
  refreshKey?: number | string;
  onVoided: (result: VoidPaymentResult) => void;
}

const REASON_MAX = 100;

export const PAYMENT_TYPE_LABELS: Record<string, string> = {
  cash: 'Μετρητά',
  card: 'Κάρτα',
  iris: 'IRIS',
  credit: 'Επί πιστώσει',
  deposit: 'Προκαταβολή',
  other: 'Λοιπά',
};

/** Το χρώμα της ετικέτας: «Επί πιστώσει» και «Προκαταβολή» ξεχωρίζουν (κανένα χρήμα στο συρτάρι). */
const typeBadgeClass = (type: string) => (type === 'credit'
  ? 'bg-amber-500/20 text-amber-800 dark:text-amber-300'
  : type === 'deposit'
    ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300'
    : 'bg-surface-3 text-t1');

const euro = (n: number) => `${formatMoney((Number(n) || 0))}`;
const time = (iso: string | null) => (iso
  ? new Date(iso).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })
  : '');

/** Η εξήγηση δίπλα σε πληρωμή που ΔΕΝ ακυρώνεται από εδώ (null = καμία). */
export function voidHint(payment: TablePayment): string | null {
  if (payment.voidedAt) return null;
  // Προκαταβολή κράτησης: πληρώθηκε online (Viva) πριν έρθει ο πελάτης — δεν ακυρώνεται από το ταμείο.
  if (payment.type === 'deposit') return 'Προπληρωμένη online με την κράτηση.';
  // 'not_cash' = κάρτα/IRIS: πέρασαν από τερματικό, η επιστροφή γίνεται εκεί.
  if (payment.voidBlock === 'not_cash') return 'Κάρτα/IRIS επιστρέφονται στο τερματικό.';
  if (payment.voidBlock === 'closed_period') return 'Ανήκει σε κλεισμένο ταμείο (Ζ) — δεν ακυρώνεται.';
  return null;
}

const TablePaymentsList: React.FC<Props> = ({ storeId, sessionId, canVoid, refreshKey, onVoided }) => {
  const [summary, setSummary] = useState<TablePaymentsSummary | null>(null);
  const [voiding, setVoiding] = useState<TablePayment | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setSummary(await tablePaymentsApi.summary(storeId, sessionId));
    } catch {
      setSummary(null);
    }
  }, [storeId, sessionId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const payments = summary && Array.isArray(summary.payments) ? summary.payments : [];
  if (!summary || !summary.void || !summary.void.enabled || payments.length === 0) return null;

  const startVoid = (payment: TablePayment) => {
    setVoiding(payment);
    setReason('');
    setError(null);
  };

  const cleanReason = reason.replace(/\s+/g, ' ').trim();

  const submit = async () => {
    if (!voiding) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await tablePaymentsApi.voidPayment(storeId, voiding.id, cleanReason);
      setVoiding(null);
      setReason('');
      onVoided(result);
      load();
    } catch (e) {
      setError(e instanceof DineInApiError ? e.message : 'Αποτυχία σύνδεσης με τον διακομιστή.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="table-payments" className="px-4 pt-4 pb-2">
      <p className="text-sm font-bold text-t2 uppercase tracking-wide mb-2">Πληρωμές τραπεζιού</p>
      <div className="space-y-2">
        {payments.map(p => {
          const voided = !!p.voidedAt;
          const hint = voidHint(p);
          const label = PAYMENT_TYPE_LABELS[p.type] || p.type;
          return (
            <div
              key={p.id}
              data-testid={`table-payment-${p.id}`}
              className={`rounded-xl border px-3 py-2.5 ${voided ? 'border-line bg-surface-2/60 opacity-75' : 'border-line bg-surface-1'}`}
            >
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${typeBadgeClass(p.type)}`}>{label}</span>
                <span className="text-xs text-t3 truncate">
                  {time(p.processedAt)}{p.processedByName ? ` · ${p.processedByName}` : ''}
                </span>
                <span className={`ml-auto text-sm font-semibold ${voided ? 'line-through text-t4' : 'text-t1'}`}>
                  {euro(p.amount)}
                </span>
                {!voided && p.voidable && canVoid && voiding?.id !== p.id && (
                  <button
                    type="button"
                    onClick={() => startVoid(p)}
                    title="Ακύρωση λάθος πληρωμής"
                    className="ml-1 flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-red-500/40 text-red-700 dark:text-red-300 text-xs font-semibold hover:bg-red-500/10"
                  >
                    <Undo2 className="w-3.5 h-3.5" />
                    Ακύρωση
                  </button>
                )}
              </div>

              {voided && (
                <p className="text-xs text-t3 mt-1">
                  Ακυρώθηκε {time(p.voidedAt)}{p.voidedByName ? ` από ${p.voidedByName}` : ''}
                  {p.voidedReason ? `: ${p.voidedReason}` : ''}
                </p>
              )}
              {hint && <p className="text-xs text-t3 mt-1">{hint}</p>}

              {voiding?.id === p.id && (
                <div className="mt-2 space-y-2">
                  <label className="block text-xs font-semibold text-t2" htmlFor={`void-reason-${p.id}`}>
                    Λόγος ακύρωσης (προαιρετικός)
                  </label>
                  <input
                    id={`void-reason-${p.id}`}
                    type="text"
                    value={reason}
                    maxLength={REASON_MAX}
                    autoFocus
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="π.χ. λάθος τρόπος πληρωμής, πλήρωσε με κάρτα"
                    className="w-full px-3 py-2 rounded-lg border border-line-strong bg-surface-1 text-t1 text-sm"
                  />
                  <p className="text-[11px] text-t4">
                    Η πληρωμή δεν σβήνεται: μένει ως ακυρωμένη με το όνομά σου, την ώρα και τον λόγο, αν γράψεις.
                    Τα είδη της ξαναγίνονται απλήρωτα. {reason.length}/{REASON_MAX}
                  </p>
                  {error && (
                    <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-2.5 py-2 text-xs text-red-700 dark:text-red-300">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => { setVoiding(null); setError(null); }}
                      disabled={submitting}
                      className="flex-1 py-2 rounded-lg border border-line-strong text-t2 text-sm font-semibold disabled:opacity-50"
                    >
                      Πίσω
                    </button>
                    <button
                      type="button"
                      onClick={submit}
                      disabled={submitting}
                      className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                      Ακύρωση πληρωμής {euro(p.amount)}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TablePaymentsList;
